import { useNavigate } from "react-router-dom";
import { Sparkles, Waypoints, Gauge } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import { PrimaryButton, SecondaryButton, Stat } from "@/components/brand";

const HERO_IMG = "https://images.pexels.com/photos/6169135/pexels-photo-6169135.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=800";

const FEATURES = [
  { icon: Sparkles, title: "AI-powered routing", body: "Routa's routing engine finds the fastest, most efficient way to deliver every order — automatically sequencing stops and matching the right driver." },
  { icon: Waypoints, title: "Real-time tracking", body: "Live driver location, ETAs, proof of delivery, and shareable customer tracking links in one clean dispatch console." },
  { icon: Gauge, title: "Scales with you", body: "From same-hour drops to multi-location operations — a last-mile foundation that grows with your business across Canada." },
];

export default function Home() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <MarketingNav />

      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="animate-fade-up">
            <div className="flex items-center gap-4 mb-8">
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">[01] Last-Mile Network</span>
              <span className="h-px w-16 bg-gray-300" />
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">Vancouver · Canada</span>
            </div>
            <h1 className="headline text-5xl lg:text-[4.5rem] text-[#0a0a0a]">
              AI-powered last-mile <span className="text-orange-500">delivery</span> for modern businesses.
            </h1>
            <p className="mt-7 text-base text-gray-500 max-w-md leading-relaxed">
              Routa uses AI and intelligent routing to help businesses find the fastest, most efficient way to deliver their products. Manage drivers, dispatch orders, and track every delivery in real time.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <PrimaryButton orange arrow data-testid="hero-apply-button" onClick={() => navigate("/register")}>
                Start delivering
              </PrimaryButton>
              <SecondaryButton data-testid="hero-login-button" onClick={() => navigate("/pricing")}>
                View pricing
              </SecondaryButton>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg border-l border-gray-200 pl-6">
              <Stat label="Launch city" value="Vancouver" />
              <Stat label="Routing" value="AI-driven" />
              <Stat label="Tracking" value="Real-time" />
            </div>
          </div>

          {/* Image panel */}
          <div className="relative aspect-[4/5] w-full overflow-hidden animate-fade-up" style={{ animationDelay: "120ms" }}>
            <img src={HERO_IMG} alt="Last-mile delivery driver with packages" className="absolute inset-0 h-full w-full object-cover grayscale" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/85 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/60">Last-Mile Node</div>
              <div className="font-mono text-sm text-white mt-1">YVR-01 · 49.2827°N 123.1207°W</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-gray-100 py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white p-8">
                <f.icon className="h-6 w-6 text-orange-500" strokeWidth={1.8} />
                <h3 className="mt-6 text-xl font-bold text-[#0a0a0a]">{f.title}</h3>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="bg-[#0a0a0a] py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div>
            <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/40 mb-5">[02] Get Started</div>
            <h2 className="headline text-3xl lg:text-5xl text-white">Deliver smarter with AI-powered routing.</h2>
          </div>
          <PrimaryButton orange arrow data-testid="cta-apply-button" onClick={() => navigate("/pricing")} className="shrink-0">
            View pricing
          </PrimaryButton>
        </div>
      </section>

      <Footer />
    </div>
  );
}
